import * as React from "react";
import { inventory } from "@fm/api";
import type { ApiClient } from "@fm/api/types";
import type { VehicleInventory } from "@fm/api";
import {
  parseLocalDate,
  Results,
  type VehicleResult,
  type ResultsSearchParams,
} from "@fm/ui";
import { getApiBaseUrl } from "../lib/config";
import { resolveEmbedDate } from "../utils/embedDates";
import { primaryColorStyle } from "../utils/primaryColor";
import { useBusinessProfile } from "../hooks/useBusinessProfile";
import { useFleetRegions } from "../hooks/useFleetRegions";
import { EmbedHeaderContainer } from "./EmbedHeaderContainer";
import type { EmbedSearchBarFormData } from "./EmbedSearchBar";
import {
  applyFilters,
  filterBySleeps,
  parseFilterParams,
  resolveSelection,
  serializeFilterParams,
  sortVehicles,
  type FilterContext,
  type FilterSelection,
  type SortKey,
} from "./rv-search-filters/filters";
import {
  FilterEmptyActions,
  RVSearchFilters,
} from "./rv-search-filters/RVSearchFilters";

/** Payload emitted when a visitor reserves/selects a vehicle. */
export interface RVSearchReservePayload {
  vehicle_id: string;
  vehicle_name: string;
  total_price: number;
  search_params: ResultsSearchParams;
}

/** Initial location/proximity context for the search. */
export interface RVSearchLocation {
  lat: number | null;
  lng: number | null;
  city?: string;
  state?: string;
  locationText?: string;
}

export interface RVSearchContainerProps {
  apiKey: string;
  fleetId: string;
  apiBaseUrl?: string;
  /** Initial search criteria. `from`/`to` accept relative tokens (today, tomorrow, today+N). */
  searchParams: ResultsSearchParams;
  location?: RVSearchLocation;
  radiusMiles?: number;
  /**
   * The region the customer is shopping in, or `""`/omitted for every one.
   * A prop, not a fetch: it comes from the URL/query params this container
   * (and the page above it) was given, which is where a shared link or a
   * re-search puts it. `EmbedHeaderContainer` learns the fleet's *list* of
   * regions on its own from `/config` — see `useFleetRegions` — but which
   * one is currently active stays the host's concern, the same way
   * `location`/`searchParams` already are.
   */
  regionId?: string;
  theme?: "light" | "dark";
  primaryColor?: string;
  googleMapsApiKey?: string;
  currency?: string;
  className?: string;
  /**
   * Fallback logo shown when the fleet has no `logo_url`. Use an ABSOLUTE URL
   * when embedding as a web component on a third-party site — a relative path
   * resolves against the host page and will 404.
   */
  defaultLogoUrl?: string;
  /** Absolute app origin for the header logo's booking-page link (web-component
   *  use on third-party sites; omit inside same-origin iframes). */
  appBaseUrl?: string;
  /** Fired when a vehicle card is reserved/selected. The host owns navigation. */
  onReserve?: (vehicleData: RVSearchReservePayload) => void;
  onVehicleSelect?: (vehicleData: RVSearchReservePayload) => void;
  /** Fired when the header search is submitted with new criteria. */
  onSearch?: (data: EmbedSearchBarFormData) => void;
  /** When provided, vehicle cards render as real anchors (`<a href>`). */
  getVehicleHref?: (vehicle: VehicleResult) => string;
  /**
   * Filters and sort to start from, as the page address params that hold them
   * (`rv_type`, `price_min`, `price_max`, `pet_friendly`, `delivery`, `sort`).
   * Read once, like `searchParams`; values the results do not have are ignored.
   */
  initialFilters?: Record<string, string>;
  /**
   * Fired when the shopper changes a filter or the sort, with the params that
   * now hold them (an absent key means "not set"). The host owns navigation:
   * the page writes them to its address, a web component host may ignore them.
   */
  onFiltersChange?: (params: Record<string, string>) => void;
}

/** Internal, resolved search criteria driving the proximity query. */
interface RVSearchCriteria {
  from: string;
  to: string;
  adults: number;
  children: number;
  infants: number;
  pets: number;
  lat: number | null;
  lng: number | null;
  city: string;
  state: string;
  locationText: string;
  radiusMiles: number;
  openInNewTab?: string;
}

/**
 * The nights between pick-up and return, at least 1. The dates come straight
 * from the page address, which may carry none (the default embed snippet, a
 * search bar submitted without dates) or something that is not `YYYY-MM-DD`,
 * and `parseLocalDate` throws on those; the page must still render, so they
 * count as 1 night, like a stay with no length.
 */
function countNights(from: string, to: string): number {
  let rawNights: number;
  try {
    rawNights = Math.round(
      (parseLocalDate(to).getTime() - parseLocalDate(from).getTime()) /
        86_400_000,
    );
  } catch {
    return 1;
  }
  return Number.isFinite(rawNights) ? Math.max(1, rawNights) : 1;
}

function processVehicles(
  vehicles: VehicleInventory[],
  from: string,
  to: string,
): VehicleResult[] {
  return vehicles
    .map((vehicle) => {
      const isAvailable = vehicle.daily_inventory.every((day) => day.available);
      if (!isAvailable) return null;

      const hasValidRates = vehicle.daily_inventory.every(
        (day) => day.rate !== null,
      );
      if (!hasValidRates) return null;

      const includedDays = vehicle.daily_inventory.filter((day) => {
        const dayDate = parseLocalDate(day.date);
        const pickUpDate = parseLocalDate(from);
        const returnDate = parseLocalDate(to);
        return dayDate >= pickUpDate && dayDate < returnDate;
      });

      const totalPrice = includedDays.reduce(
        (sum, day) => sum + (day.rate ?? 0),
        0,
      );
      const averageRate =
        includedDays.length > 0 ? totalPrice / includedDays.length : 0;

      return {
        id: vehicle.id,
        name: vehicle.name,
        year: vehicle.year,
        category: vehicle.category,
        equipment_type: vehicle.equipment_type,
        vehicle_type: vehicle.vehicle_type,
        sleeps: vehicle.sleeps,
        length_feet: vehicle.length_feet,
        photos: vehicle.photos,
        daily_inventory: vehicle.daily_inventory,
        totalPrice,
        averageRate,
        rv_class: vehicle.rv_class,
        pets_allowed: vehicle.pets_allowed,
        delivery_available: vehicle.delivery_available,
        festivals_events_allowed: vehicle.festivals_events_allowed,
        unlimited_mileage: vehicle.unlimited_mileage,
        amenities: vehicle.amenities,
        search_booking_mode: vehicle.booking_mode,
      };
    })
    .filter(Boolean) as VehicleResult[];
}

/**
 * Framework-agnostic container for proximity-based RV search. Mirrors the
 * `/embed/rv/search` page but exposes navigation as callbacks/events so it can
 * be used both inside the Next.js app and as the `<fm-rv-search>` web component
 * on third-party sites.
 */
export function RVSearchContainer({
  apiKey,
  fleetId,
  apiBaseUrl,
  searchParams,
  location,
  radiusMiles = 50,
  regionId,
  theme = "light",
  primaryColor: primaryColorProp,
  googleMapsApiKey,
  currency = "USD",
  className,
  defaultLogoUrl,
  appBaseUrl,
  onReserve,
  onVehicleSelect,
  onSearch,
  getVehicleHref,
  initialFilters,
  onFiltersChange,
}: RVSearchContainerProps) {
  const [criteria, setCriteria] = React.useState<RVSearchCriteria>(() => ({
    from: resolveEmbedDate(searchParams.from),
    to: resolveEmbedDate(searchParams.to),
    adults: searchParams.adults || 2,
    children: searchParams.children || 0,
    infants: searchParams.infants || 0,
    pets: searchParams.pets || 0,
    lat: location?.lat ?? null,
    lng: location?.lng ?? null,
    city: location?.city || "",
    state: location?.state || "",
    locationText: location?.locationText || "",
    radiusMiles,
    openInNewTab: searchParams.open_in_new_tab,
  }));

  // What the shopper (or the page address) picked. Kept raw: values the
  // results do not have are ignored when it is resolved, never lost.
  const [selection, setSelection] = React.useState<FilterSelection>(
    () => parseFilterParams(initialFilters ?? {}).selection,
  );
  const [sort, setSort] = React.useState<SortKey>(
    () => parseFilterParams(initialFilters ?? {}).sort,
  );

  const [vehicles, setVehicles] = React.useState<VehicleResult[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const { primaryColor: profilePrimaryColor, isLoading: isProfileLoading } =
    useBusinessProfile(apiKey || "", apiBaseUrl);
  const primaryColor = primaryColorProp || profilePrimaryColor;

  // "All regions" with no typed location has no point and no region to send,
  // and the server refuses that request on purpose (an empty body must never
  // dump the whole fleet). The container asks once per published region
  // instead, so the fleet's regions are needed here and not only in the header.
  const { regions: fleetRegions, isLoading: isRegionsLoading } =
    useFleetRegions(apiKey || "", apiBaseUrl);
  const everyRegionId = React.useMemo(
    () => (fleetRegions ?? []).map((region) => region.id),
    [fleetRegions],
  );

  const apiClient = React.useMemo<ApiClient>(
    () => ({
      apiKey: apiKey || "",
      baseUrl: getApiBaseUrl(apiBaseUrl),
    }),
    [apiKey, apiBaseUrl],
  );

  // Load vehicles based on proximity search
  React.useEffect(() => {
    const { from, to, lat, lng, radiusMiles: radius } = criteria;
    const hasPoint = lat !== null && lng !== null;
    // A region alone is a valid search — the server answers with the
    // region's whole published inventory. A point alone is valid too. With
    // neither, "All regions" is asked region by region; a fleet that
    // publishes none has nothing to ask and nothing to show.
    const regionTargets: (string | undefined)[] = regionId
      ? [regionId]
      : hasPoint
        ? [undefined]
        : everyRegionId;
    if (!apiKey || !from || !to || regionTargets.length === 0) return;

    const loadVehicles = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const responses = await Promise.all(
          regionTargets.map((target) =>
            inventory.proximitySearch(apiClient, {
              // Spread both the point and the region so that a region-only
              // search sends no `lat`/`lng` key at all, rather than `null`
              // values the server would have to interpret.
              ...(hasPoint ? { lat, lng, radius_miles: radius } : {}),
              from_date: from,
              to_date: to,
              // Spread so that "every region" sends no key at all, rather
              // than an empty one the server would have to interpret.
              ...(target ? { region_id: target } : {}),
            }),
          ),
        );

        const answered = responses.filter(
          (response) => response?.data?.vehicles,
        );
        if (answered.length > 0) {
          // A vehicle belongs to one region, so the union has no repeats; the
          // id check only guards against a server that says otherwise.
          const seen = new Set<string>();
          const union = answered
            .flatMap((response) => response.data.vehicles)
            .filter((vehicle) => !seen.has(vehicle.id) && seen.add(vehicle.id));
          setVehicles(processVehicles(union, from, to));
        }
      } catch (err: unknown) {
        console.error("Failed to load vehicles:", err);
        setError("Failed to load available vehicles. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    loadVehicles();
  }, [apiClient, apiKey, criteria, regionId, everyRegionId]);

  const handleHeaderSearch = React.useCallback(
    (data: EmbedSearchBarFormData) => {
      setCriteria((prev) => ({
        ...prev,
        from: resolveEmbedDate(data.from),
        to: resolveEmbedDate(data.to),
        adults: data.adults,
        children: data.children,
        infants: data.infants,
        pets: data.pets,
        lat: data.location?.lat ?? prev.lat,
        lng: data.location?.lng ?? prev.lng,
        city: data.location?.city ?? prev.city,
        state: data.location?.state ?? prev.state,
        locationText: data.locationText || prev.locationText,
      }));

      // Notify the host (e.g. to sync the URL) without owning navigation.
      onSearch?.(data);
    },
    [onSearch],
  );

  // Mirrors the fetch effect's own bail condition: a search actually ran
  // (or is about to) whenever there is a point or a region to search from.
  const hasSearchPoint = criteria.lat !== null && criteria.lng !== null;
  const hasSearched = hasSearchPoint || !!regionId || everyRegionId.length > 0;
  // Until `/config` answers, "no region to ask" is not yet the truth; showing
  // the pickup prompt for a moment would be wrong more often than not.
  const awaitingRegions = isRegionsLoading && !hasSearchPoint && !regionId;
  // The fetch effect also bails without both dates; Results asks for them.
  const missingDates = !criteria.from || !criteria.to;

  const searchResultsParams: ResultsSearchParams = {
    from: criteria.from,
    to: criteria.to,
    adults: criteria.adults,
    children: criteria.children,
    infants: criteria.infants,
    pets: criteria.pets,
    open_in_new_tab: criteria.openInNewTab,
  };

  const guests = criteria.adults + criteria.children;
  const nights = countNights(criteria.from, criteria.to);
  const filterContext = React.useMemo<FilterContext>(
    () => ({ nights, pets: criteria.pets, currency }),
    [nights, criteria.pets, currency],
  );
  // Guests are part of the search ("sleeps at least N"), not a filter: what
  // they remove is never offered back.
  const searchable = React.useMemo(
    () => filterBySleeps(vehicles, guests),
    [vehicles, guests],
  );
  const resolved = React.useMemo(
    () => resolveSelection(selection, searchable, filterContext),
    [selection, searchable, filterContext],
  );
  const filtered = React.useMemo(
    () => applyFilters(searchable, resolved, filterContext),
    [searchable, resolved, filterContext],
  );
  const shownVehicles = React.useMemo(
    () => sortVehicles(filtered, sort, nights),
    [filtered, sort, nights],
  );

  const handleSelectionChange = (next: FilterSelection) => {
    setSelection(next);
    onFiltersChange?.(serializeFilterParams(next, sort));
  };
  const handleSortChange = (next: SortKey) => {
    setSort(next);
    onFiltersChange?.(serializeFilterParams(resolved, next));
  };

  const handleReserve = (vehicleData: RVSearchReservePayload) => {
    onReserve?.(vehicleData);
  };

  const handleVehicleSelect = (vehicleData: RVSearchReservePayload) => {
    (onVehicleSelect || onReserve)?.(vehicleData);
  };

  if (!apiKey) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-sm text-gray-600">You should provide an API key</p>
        </div>
      </div>
    );
  }

  if (isProfileLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const locationLabel =
    criteria.city && criteria.state
      ? `${criteria.city}, ${criteria.state}`
      : criteria.locationText || null;

  return (
    <div className={className} style={primaryColorStyle(primaryColor)}>
      <EmbedHeaderContainer
        apiKey={apiKey}
        apiBaseUrl={apiBaseUrl}
        defaultLogoUrl={defaultLogoUrl}
        theme={theme}
        primaryColor={primaryColor || undefined}
        googleMapsApiKey={googleMapsApiKey}
        openInNewTab={criteria.openInNewTab === "true"}
        appBaseUrl={appBaseUrl}
        initialSearchData={{
          locationText: criteria.locationText || (locationLabel ?? ""),
          location:
            criteria.lat !== null && criteria.lng !== null
              ? {
                  lat: criteria.lat,
                  lng: criteria.lng,
                  city: criteria.city,
                  state: criteria.state,
                  formatted_address: locationLabel || "",
                }
              : null,
          from: criteria.from,
          to: criteria.to,
          adults: criteria.adults,
          children: criteria.children,
          infants: criteria.infants,
          pets: criteria.pets,
          // So the picker starts on the region this search actually ran
          // under, not always on "All regions" regardless of the URL.
          regionId,
        }}
        onSearch={handleHeaderSearch}
      />
      <div className="p-4">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Search Results
          </h2>
          <p className="text-sm text-gray-500">
            {locationLabel ? `${locationLabel} • ` : ""}
            {guests} guest
            {guests !== 1 ? "s" : ""}
          </p>
        </div>
        <RVSearchFilters
          vehicles={searchable}
          selection={resolved}
          sort={sort}
          ctx={filterContext}
          shownCount={filtered.length}
          theme={theme}
          onSelectionChange={handleSelectionChange}
          onSortChange={handleSortChange}
        />
        <Results
          vehicles={shownVehicles}
          searchParams={searchResultsParams}
          isLoading={isLoading || awaitingRegions}
          error={error}
          selectedVehicle={null}
          hasSearched={hasSearched}
          missingDates={missingDates}
          theme={theme}
          onReserve={handleReserve}
          onVehicleSelect={handleVehicleSelect}
          getVehicleHref={getVehicleHref}
          showBackButton={false}
          currency={currency}
          showReserveButton={true}
          showHeader={false}
          {...(searchable.length > 0 && filtered.length === 0
            ? {
                emptyMessage:
                  "No RVs match all of these filters for your dates and group size.",
                emptyActions: (
                  <FilterEmptyActions
                    vehicles={searchable}
                    selection={resolved}
                    ctx={filterContext}
                    onSelectionChange={handleSelectionChange}
                  />
                ),
              }
            : {})}
        />
      </div>
    </div>
  );
}
